import {getAge} from "./aboutManager";
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Chip,
    Stack,
    Divider,
    Fade
} from "@mui/material";
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

function About() {
    const age = getAge(new Date(856_220_400_000));

    const education = [
        {
            year: '2019-2020',
            title: 'Exchange Student - Laval University',
            location: 'Québec, Canada',
            description: 'Studied video games, AI, and mobile development. Represented Laval University in the Ubisoft Game Competition in Québec.',
            icon: <EmojiEventsIcon/>,
            color: 'secondary'
        },
        {
            year: '2017-2020',
            title: 'Engineering Degree - ENSSAT',
            location: 'Lannion, France',
            description: 'École Nationale Supérieure des Sciences Appliquées et de Technologie. Specialized in computer science.',
            icon: <SchoolIcon/>,
            color: 'primary'
        },
        {
            year: '2015-2017',
            title: 'Preparatory Classes',
            location: 'France',
            description: 'Higher school preparatory classes (Scientific section). Developed strong communication skills through khôles (oral examinations).',
            icon: <SchoolIcon/>,
            color: 'primary'
        }
    ];

    return (
        <Container maxWidth="lg" sx={{py: {xs: 4, md: 8}}}>
            <Fade in={true} timeout={800}>
                <Box>
                    {/* Header */}
                    <Box sx={{textAlign: 'center', mb: {xs: 6, md: 8}}}>
                        <Typography
                            variant="h2"
                            component="h1"
                            gutterBottom
                            sx={{
                                fontWeight: 800,
                                background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                            }}
                        >
                            About Me
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary" sx={{maxWidth: 600, mx: 'auto'}}>
                            Full-stack developer passionate about creating beautiful digital experiences
                        </Typography>
                    </Box>

                    {/* Introduction Card */}
                    <Card
                        sx={{
                            mb: 6,
                            background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(30, 64, 175, 0.02) 100%)',
                            border: '1px solid rgba(37, 99, 235, 0.1)',
                        }}
                    >
                        <CardContent sx={{p: {xs: 3, sm: 4}}}>
                            <Typography
                                variant="h5"
                                sx={{
                                    lineHeight: 1.8,
                                    color: 'text.primary',
                                    fontSize: {xs: '1.125rem', sm: '1.25rem'}
                                }}
                            >
                                I'm <strong>Paul-Louis Renard</strong>, {age} years old, a software engineer working
                                on <Chip label="Node.js" size="small" sx={{mx: 0.5}}/> servers,
                                <Chip label="Android" size="small" sx={{mx: 0.5}}/> and
                                <Chip label="iOS" size="small" sx={{mx: 0.5}}/> platforms.
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Education Timeline */}
                    <Box sx={{mb: 6}}>
                        <Typography variant="h3" gutterBottom fontWeight={700} sx={{mb: 4}}>
                            Education
                        </Typography>

                        <Timeline position="alternate">
                            {education.map((item, index) => (
                                <TimelineItem key={index}>
                                    <TimelineOppositeContent
                                        sx={{
                                            m: 'auto 0',
                                            display: {xs: 'none', sm: 'block'}
                                        }}
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        <Typography variant="h6" fontWeight={600}>
                                            {item.year}
                                        </Typography>
                                    </TimelineOppositeContent>

                                    <TimelineSeparator>
                                        <TimelineConnector sx={{bgcolor: `${item.color}.light`}}/>
                                        <TimelineDot color={item.color} variant="outlined" sx={{p: 1}}>
                                            {item.icon}
                                        </TimelineDot>
                                        <TimelineConnector sx={{bgcolor: `${item.color}.light`}}/>
                                    </TimelineSeparator>

                                    <TimelineContent sx={{py: '12px', px: 2}}>
                                        <Card
                                            sx={{
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                }
                                            }}
                                        >
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary"
                                                            sx={{display: {xs: 'block', sm: 'none'}, mb: 1}}>
                                                    {item.year}
                                                </Typography>
                                                <Typography variant="h6" component="h3" fontWeight={700} gutterBottom>
                                                    {item.title}
                                                </Typography>
                                                <Typography variant="subtitle2" color="primary.main" gutterBottom>
                                                    {item.location}
                                                </Typography>
                                                <Divider sx={{my: 1.5}}/>
                                                <Typography variant="body2" color="text.secondary">
                                                    {item.description}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </TimelineContent>
                                </TimelineItem>
                            ))}
                        </Timeline>
                    </Box>

                    {/* Skills Section */}
                    <Box>
                        <Typography variant="h3" gutterBottom fontWeight={700} sx={{mb: 3}}>
                            Technical Skills
                        </Typography>

                        <Card>
                            <CardContent sx={{p: {xs: 3, sm: 4}}}>
                                <Stack spacing={3}>
                                    <Box>
                                        <Typography variant="h6" gutterBottom fontWeight={600}
                                                    color="primary.main">
                                            Backend Development
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                            {['Node.js', 'Express', 'Firebase', 'REST APIs'].map((skill) => (
                                                <Chip key={skill} label={skill} variant="outlined"/>
                                            ))}
                                        </Stack>
                                    </Box>

                                    <Divider/>

                                    <Box>
                                        <Typography variant="h6" gutterBottom fontWeight={600}
                                                    color="primary.main">
                                            Frontend Development
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                            {['React', 'Material-UI', 'JavaScript', 'TypeScript'].map((skill) => (
                                                <Chip key={skill} label={skill} variant="outlined"/>
                                            ))}
                                        </Stack>
                                    </Box>

                                    <Divider/>

                                    <Box>
                                        <Typography variant="h6" gutterBottom fontWeight={600}
                                                    color="primary.main">
                                            Mobile Development
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                            {['Android', 'iOS', 'React Native'].map((skill) => (
                                                <Chip key={skill} label={skill} variant="outlined"/>
                                            ))}
                                        </Stack>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            </Fade>
        </Container>
    );
}

export default About;